<template>
  <section class="customer-settings-tab">
    <header class="customer-settings-tab__header">
      <h4>{{ text.detail.settingsTab.title }}</h4>
      <p>{{ text.detail.settingsTab.description }}</p>
    </header>

    <div class="customer-settings-tab__cards">
      <article class="customer-settings-tab__card customer-settings-tab__card--permissions">
        <div class="customer-settings-tab__card-head">
          <h5>{{ text.detail.settingsTab.cards.permissions.title }}</h5>
          <span class="customer-settings-tab__visibility-pill">{{ visibilityLabel }}</span>
        </div>
        <p>{{ text.detail.settingsTab.cards.permissions.description }}</p>
        <button
          type="button"
          class="app-header-btn app-header-btn--secondary"
          @click="$emit('open-permissions')"
        >
          {{ text.detail.settingsTab.cards.permissions.action }}
        </button>
      </article>

      <article class="customer-settings-tab__card customer-settings-tab__card--merge">
        <h5>{{ text.detail.settingsTab.cards.merge.title }}</h5>
        <p>{{ text.detail.settingsTab.cards.merge.description }}</p>
        <p class="customer-settings-tab__warning">{{ text.detail.settingsTab.cards.merge.warning }}</p>
        <button
          type="button"
          class="app-header-btn app-header-btn--secondary"
          @click="$emit('open-merge')"
        >
          {{ text.detail.settingsTab.cards.merge.action }}
        </button>
      </article>

      <article class="customer-settings-tab__card customer-settings-tab__card--archive">
        <h5>{{ archiveCardTitle }}</h5>
        <p>{{ archiveCardDescription }}</p>
        <button
          type="button"
          class="app-header-btn app-header-btn--secondary"
          @click="$emit('archive-toggle')"
        >
          {{ archiveCardActionLabel }}
        </button>
      </article>

      <article class="customer-settings-tab__card customer-settings-tab__card--danger">
        <h5>{{ text.detail.settingsTab.cards.delete.title }}</h5>
        <p>{{ text.detail.settingsTab.cards.delete.description }}</p>
        <button
          type="button"
          class="app-header-btn app-header-btn--delete"
          @click="$emit('delete-customer')"
        >
          {{ text.detail.settingsTab.cards.delete.action }}
        </button>
      </article>
    </div>
  </section>
</template>

<script>
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');

export default {
  name: 'CustomerDetailSettingsTab',
  emits: ['open-permissions', 'open-merge', 'archive-toggle', 'delete-customer'],
  props: {
    customer: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      text,
    };
  },
  computed: {
    isCustomerActive() {
      return Number(this.customer?.active ?? 0) === 1;
    },
    archiveCardTitle() {
      return this.isCustomerActive
        ? this.text.detail.settingsTab.cards.archive.title
        : this.text.detail.settingsTab.cards.unarchive.title;
    },
    archiveCardDescription() {
      return this.isCustomerActive
        ? this.text.detail.settingsTab.cards.archive.description
        : this.text.detail.settingsTab.cards.unarchive.description;
    },
    archiveCardActionLabel() {
      return this.isCustomerActive
        ? this.text.detail.settingsTab.cards.archive.action
        : this.text.detail.settingsTab.cards.unarchive.action;
    },
    visibilityMode() {
      return String(
        this.customer?.visibility_settings?.mode
        || this.customer?.visibility
        || 'public'
      ).trim().toLowerCase();
    },
    visibilityLabel() {
      if (this.visibilityMode === 'private') {
        return this.text.detail.settingsTab.cards.permissions.statusPrivate;
      }

      if (this.visibilityMode === 'hidden') {
        return this.text.detail.settingsTab.cards.permissions.statusHidden;
      }

      return this.text.detail.settingsTab.cards.permissions.statusPublic;
    },
  },
};
</script>

<style scoped>
.customer-settings-tab {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.customer-settings-tab__header h4 {
  margin: 0;
  font-size: 26px;
  line-height: 32px;
  color: #1f2937;
}

.customer-settings-tab__header p {
  margin: 6px 0 0;
  color: #6b7280;
  font-size: 14px;
}

.customer-settings-tab__cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.customer-settings-tab__card {
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 10px 24px -22px rgba(15, 23, 42, 0.45);
  min-height: 198px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.customer-settings-tab__card::before {
  content: '';
  width: 54px;
  height: 4px;
  border-radius: 999px;
  margin-bottom: 10px;
  background: rgba(148, 163, 184, 0.35);
}

.customer-settings-tab__card--permissions::before {
  background: linear-gradient(90deg, #00b2e2 0%, #0070cd 100%);
}

.customer-settings-tab__card--merge::before {
  background: linear-gradient(90deg, #4dbfb4 0%, #00a17d 100%);
}

.customer-settings-tab__card--archive::before {
  background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
}

.customer-settings-tab__card-head {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.customer-settings-tab__visibility-pill {
  border: 1px solid rgba(0, 112, 205, 0.34);
  background: rgba(0, 112, 205, 0.08);
  color: #005091;
  border-radius: 999px;
  font-size: 11px;
  line-height: 16px;
  padding: 3px 8px;
  font-weight: 700;
  white-space: nowrap;
}

.customer-settings-tab__card h5 {
  margin: 0;
  font-size: 17px;
  line-height: 24px;
  color: #111827;
}

.customer-settings-tab__card p {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 20px;
  color: #4b5563;
}

.customer-settings-tab__card:not(.customer-settings-tab__card--danger):hover {
  border-color: rgba(0, 112, 205, 0.32);
  box-shadow: 0 18px 35px -28px rgba(0, 112, 205, 0.55);
  transform: translateY(-1px);
}

.customer-settings-tab__warning {
  color: #b45309;
  font-weight: 600;
}

.customer-settings-tab__card .app-header-btn {
  margin-top: auto;
  align-self: center;
  min-width: 164px;
  justify-content: center;
  text-align: center;
}

.customer-settings-tab__card--danger {
  border-color: rgba(225, 29, 72, 0.28);
  background: linear-gradient(180deg, #ffffff 0%, #fff5f7 100%);
}

@media (max-width: 1240px) {
  .customer-settings-tab__cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .customer-settings-tab__cards {
    grid-template-columns: 1fr;
  }
}
</style>
